import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Plus, Upload, Link, Pencil, ChevronUp, ChevronDown, X, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TAG_OPTIONS = [
  { value: 'gold', label: 'Gold' },
  { value: 'red', label: 'Red' },
  { value: 'green', label: 'Green' },
  { value: 'orange', label: 'Orange' },
  { value: 'purple', label: 'Purple' },
];

const emptyForm = {
  title: '', media_url: '', media_type: 'video',
  thumbnail_url: '', tag_label: '', tag_style: 'gold',
  inputMode: 'url', thumbMode: 'none',
};

function getYTId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

const inp = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const lbl = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 };

export default function AdminCarousel({ token: tokenProp, onRefresh }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const fileRef = useRef(null);
  const editFileRef = useRef(null);
  const thumbFileRef = useRef(null);
  const editThumbFileRef = useRef(null);

  // Get token from prop or localStorage
  const token = tokenProp || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = () => {
    fetch(`${API}/api/carousel`)
      .then(r => r.json())
      .then(d => { if (d.success) setItems(d.items || []); })
      .catch(() => {});
  };

  useEffect(() => { fetchItems(); }, []);

  const uploadFile = async (file, onDone) => {
    setUploading(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/carousel/upload`, { 
        method: 'POST', 
        headers: headers,
        body: fd 
      });
      const data = await res.json();
      if (data.success) {
        onDone(data.url, file.type.startsWith('video/') ? 'video' : 'image');
        setMsg('✓ File uploaded successfully!');
        setTimeout(() => setMsg(''), 3000);
      } else {
        setMsg('❌ Upload failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) { 
      console.error('Upload error:', err);
      setMsg('❌ Upload error: ' + err.message); 
    }
    setUploading(false);
  };

  const handleAdd = async () => {
    if (!form.title.trim() || !form.media_url.trim()) { setMsg('❌ Title and media are required.'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API}/api/carousel`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title, media_url: form.media_url, media_type: form.media_type,
          thumbnail_url: form.thumbnail_url || null,
          tag_label: form.tag_label, tag_style: form.tag_style, display_order: items.length,
        }),
      });
      const data = await res.json();
      if (data.success) { 
        setForm(emptyForm); 
        fetchItems(); 
        if (onRefresh) onRefresh(); 
        setMsg('✅ Slide added successfully!');
        setTimeout(() => setMsg(''), 3000);
      }
      else setMsg('❌ Failed to add.');
    } catch { setMsg('❌ Error adding slide.'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this item?')) return;
    try {
      await fetch(`${API}/api/carousel/${id}`, { method: 'DELETE', headers });
      fetchItems(); if (onRefresh) onRefresh();
    } catch {}
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      title: item.title, media_url: item.media_url, media_type: item.media_type,
      thumbnail_url: item.thumbnail_url || '', tag_label: item.tag_label || '',
      tag_style: item.tag_style || 'gold', inputMode: 'url', thumbMode: item.thumbnail_url ? 'url' : 'none',
    });
  };

  const cancelEdit = () => { setEditId(null); setEditForm({}); };

  const saveEdit = async () => {
    if (!editForm.title.trim() || !editForm.media_url.trim()) { setMsg('❌ Title and media required.'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API}/api/carousel/${editId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editForm.title, media_url: editForm.media_url, media_type: editForm.media_type,
          thumbnail_url: editForm.thumbnail_url || null,
          tag_label: editForm.tag_label, tag_style: editForm.tag_style,
        }),
      });
      const data = await res.json();
      if (data.success) { 
        setMsg('✅ Saved successfully!');
        setTimeout(() => {
          cancelEdit(); 
          fetchItems(); 
          if (onRefresh) onRefresh();
          setMsg('');
        }, 1000);
      }
      else setMsg('❌ Save failed.');
    } catch { setMsg('❌ Error saving.'); }
    setSaving(false);
  };

  const reorder = async (idx, dir) => {
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const newItems = [...items];
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    setItems(newItems);
    try {
      await fetch(`${API}/api/carousel/reorder`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(newItems.map((item, i) => ({ id: item.id, display_order: i }))),
      });
      fetchItems();
      if (onRefresh) onRefresh();
    } catch {}
  };

  // ── Sub-components ─────────────────────────────────────────────────────────
  const Thumb = ({ item }) => {
    const ytId = getYTId(item.media_url);
    // For YouTube videos, always use YouTube's default thumbnail
    // For custom uploads, only use external URLs (not local file uploads)
    const thumbUrl = ytId ? 
      `https://img.youtube.com/vi/${ytId}/default.jpg` :
      (item.thumbnail_url && (item.thumbnail_url.startsWith('http://') || item.thumbnail_url.startsWith('https://')) ? 
        item.thumbnail_url : null);
    
    return (
      <div style={{ width: 56, height: 32, borderRadius: 4, overflow: 'hidden', flexShrink: 0, background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {thumbUrl ? <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" decoding="async"/> : <span style={{ fontSize: 9, color: '#6b7280' }}>{item.media_type === 'video' ? 'VID' : 'IMG'}</span>}
      </div>
    );
  };

  const MediaInput = ({ f, setF, fRef, onFileChange }) => (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {['url', 'upload'].map(mode => (
          <button key={mode} onClick={() => setF(x => ({ ...x, inputMode: mode, media_url: '' }))}
            style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: f.inputMode === mode ? '#1d4ed8' : '#e5e7eb', color: f.inputMode === mode ? '#fff' : '#374151' }}>
            {mode === 'url' ? <><Link size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />URL</> : <><Upload size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Upload</>}
          </button>
        ))}
      </div>
      {f.inputMode === 'url' ? (
        <input style={inp} placeholder="https://youtu.be/... or image URL"
          value={f.media_url} onChange={e => setF(x => ({ ...x, media_url: e.target.value }))} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input ref={fRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={onFileChange} />
          <button onClick={() => fRef.current?.click()} disabled={uploading}
            style={{ padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#e5e7eb', color: '#374151' }}>
            {uploading ? 'Uploading…' : 'Choose File'}
          </button>
          {f.media_url && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Media uploaded</span>}
        </div>
      )}
    </>
  );

  const ThumbInput = ({ f, setF, fRef, onFileChange }) => (
    <div style={{ marginBottom: 10 }}>
      <label style={lbl}>Custom Thumbnail <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional — shown on inactive slides)</span></label>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {[['none', 'None'], ['url', 'URL'], ['upload', 'Upload']].map(([mode, label]) => (
          <button key={mode} onClick={() => setF(x => ({ ...x, thumbMode: mode, ...(mode === 'none' && { thumbnail_url: '' }) }))}
            style={{ padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: f.thumbMode === mode ? '#1d4ed8' : '#e5e7eb', color: f.thumbMode === mode ? '#fff' : '#374151' }}>
            {label}
          </button>
        ))}
      </div>
      {f.thumbMode === 'url' && (
        <input style={inp} placeholder="https://example.com/thumb.jpg"
          value={f.thumbnail_url} onChange={e => setF(x => ({ ...x, thumbnail_url: e.target.value }))} />
      )}
      {f.thumbMode === 'upload' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input ref={fRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange} />
          <button onClick={() => fRef.current?.click()} disabled={uploading}
            style={{ padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#e5e7eb', color: '#374151' }}>
            {uploading ? 'Uploading…' : 'Choose Image'}
          </button>
          {f.thumbnail_url && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Thumbnail uploaded</span>}
        </div>
      )}
    </div>
  );

  const SlideForm = ({ f, setF, mFRef, tFRef, onMediaFile, onThumbFile }) => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div><label style={lbl}>Title</label>
          <input style={inp} placeholder="e.g. JOB PASS Intro" value={f.title} onChange={e => setF(x => ({ ...x, title: e.target.value }))} />
        </div>
        <div><label style={lbl}>Type</label>
          <select style={inp} value={f.media_type} onChange={e => setF(x => ({ ...x, media_type: e.target.value }))}>
            <option value="video">Video</option><option value="image">Image</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={lbl}>Media</label>
        <MediaInput f={f} setF={setF} fRef={mFRef} onFileChange={onMediaFile} />
      </div>
      <ThumbInput f={f} setF={setF} fRef={tFRef} onFileChange={onThumbFile} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div><label style={lbl}>Badge Label</label>
          <input style={inp} placeholder="e.g. NEW" maxLength={8} value={f.tag_label} onChange={e => setF(x => ({ ...x, tag_label: e.target.value }))} />
        </div>
        <div><label style={lbl}>Badge Colour</label>
          <select style={inp} value={f.tag_style} onChange={e => setF(x => ({ ...x, tag_style: e.target.value }))}>
            {TAG_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
    </>
  );

  return (
    <div style={{ fontFamily: 'sans-serif' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#111827' }}>Featured Carousel</h3>

      {/* Add form */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 20 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Add New Slide</p>
        <SlideForm
          f={form} setF={setForm} mFRef={fileRef} tFRef={thumbFileRef}
          onMediaFile={e => { const file = e.target.files[0]; if (file) uploadFile(file, (url, type) => setForm(x => ({ ...x, media_url: url, media_type: type }))); }}
          onThumbFile={e => { const file = e.target.files[0]; if (file) uploadFile(file, (url, _) => setForm(x => ({ ...x, thumbnail_url: url }))); }}
        />
        <button onClick={handleAdd} disabled={saving || uploading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: '#1d4ed8', color: '#fff', border: 'none', opacity: saving || uploading ? 0.6 : 1 }}>
          <Plus size={14} />{saving ? 'Adding…' : 'Add Slide'}
        </button>
        {msg && <p style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: msg.includes('✅') || msg.includes('✓') ? '#16a34a' : '#dc2626' }}>{msg}</p>}
      </div>

      {/* Slide list */}
      {items.length === 0 ? (
        <p style={{ fontSize: 13, color: '#6b7280' }}>No slides yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, idx) => (
            <div key={item.id}>
              {editId === item.id ? (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 10 }}>Edit Slide</p>
                  <SlideForm
                    f={editForm} setF={setEditForm} mFRef={editFileRef} tFRef={editThumbFileRef}
                    onMediaFile={e => { const file = e.target.files[0]; if (file) uploadFile(file, (url, type) => setEditForm(x => ({ ...x, media_url: url, media_type: type }))); }}
                    onThumbFile={e => { const file = e.target.files[0]; if (file) uploadFile(file, (url, _) => setEditForm(x => ({ ...x, thumbnail_url: url }))); }}
                  />
                  <div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={saveEdit} disabled={saving}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: '#16a34a', color: '#fff', border: 'none' }}>
                        <Check size={13} />{saving ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={cancelEdit}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: '#e5e7eb', color: '#374151', border: 'none' }}>
                        <X size={13} />Cancel
                      </button>
                    </div>
                    {msg && <p style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: msg.includes('✅') || msg.includes('✓') ? '#16a34a' : '#dc2626' }}>{msg}</p>}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                    <button onClick={() => reorder(idx, -1)} disabled={idx === 0}
                      style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#d1d5db' : '#6b7280', padding: 1 }}>
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => reorder(idx, 1)} disabled={idx === items.length - 1}
                      style={{ background: 'none', border: 'none', cursor: idx === items.length - 1 ? 'default' : 'pointer', color: idx === items.length - 1 ? '#d1d5db' : '#6b7280', padding: 1 }}>
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <Thumb item={item} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</p>
                    <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.media_url}</p>
                  </div>
                  {item.tag_label && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: '#e5e7eb', color: '#374151', flexShrink: 0 }}>{item.tag_label}</span>}
                  <button onClick={() => startEdit(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', padding: 4, flexShrink: 0 }}><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, flexShrink: 0 }}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
